/*
  side-cart-enhance.js
  --------------------
  Cart drawer enhancements for snippets/side-cart.liquid:
  - Amazon-style quantity stepper (minus becomes trash at the minimum)
  - Instant row / subtotal / header recalculation, background save
  - Wholesale All In One unit-price fix (unit x qty row totals)

  Lives in assets/ and is loaded once from layout/theme.liquid, because the
  theme injects the drawer HTML with JavaScript and scripts inside injected
  HTML never execute. Everything is delegated from window, and rows the
  theme re-renders later are picked up by a MutationObserver.
*/
(function () {
  if (window.__sideCartEnhanced) return;
  window.__sideCartEnhanced = true;

  var qtyTimers = new WeakMap();
  var pendingInputs = new Set();
  var saveQueue = Promise.resolve();

  // ---------- helpers ----------
  function fmt(cents) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function parseCents(text) {
    if (!text) return null;
    var cleaned = text.replace(/[^0-9.]/g, '');
    if (!cleaned) return null;
    var value = parseFloat(cleaned);
    return isNaN(value) ? null : Math.round(value * 100);
  }

  // Price text without any crossed-out compare-at price inside it
  function priceText(el) {
    var clone = el.cloneNode(true);
    clone.querySelectorAll('.old-price').forEach(function (o) { o.remove(); });
    return clone.textContent;
  }

  function listOf(el) { return el && el.closest('.side-cart-list'); }

  function isWholesale(list) {
    return !!list && list.dataset.wholesale === 'true';
  }

  function rowsOf(list) {
    return Array.prototype.slice.call(list.querySelectorAll(':scope > li[data-key]'));
  }

  function enqueue(job) {
    var run = saveQueue.then(job, job);
    saveQueue = run.catch(function () {});
    return run;
  }

  function postChange(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: parseInt(line, 10), quantity: quantity })
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart change failed: ' + res.status);
      return res.json();
    }).catch(function (err) {
      console.error('[side-cart]', err);
      // Fall back to the current cart so the drawer resyncs with reality
      return fetch('/cart.js').then(function (r) { return r.json(); }).catch(function () { return null; });
    });
  }

  // ---------- stepper ----------
  function updateStepper(input) {
    var stepper = input.closest('.cart-qty-stepper');
    if (!stepper) return;
    var minus = stepper.querySelector('.ecom-qty-minus');
    var plus = stepper.querySelector('.ecom-qty-plus');
    var min = parseInt(input.min) || 1;
    var step = parseInt(input.step) || 1;
    var max = parseInt(input.max);
    var value = parseInt(input.value) || min;
    var atMin = value - step < min;
    var canRemove = stepper.dataset.canRemove !== 'false';

    if (minus) {
      minus.classList.toggle('is-trash', atMin);
      minus.setAttribute('aria-label', atMin ? 'Remove item' : 'Decrease quantity');
      minus.disabled = atMin && !canRemove;
    }
    if (plus) plus.disabled = !isNaN(max) && value + step > max;
  }

  function clampToStep(input) {
    var min = parseInt(input.min) || 1;
    var step = parseInt(input.step) || 1;
    var max = parseInt(input.max);
    var value = parseInt(input.value) || min;
    if (value < min) value = min;
    if (step > 1) {
      var remainder = (value - min) % step;
      if (remainder !== 0) {
        value = remainder < step / 2 ? value - remainder : value + (step - remainder);
      }
    }
    if (!isNaN(max) && value > max) value = max;
    input.value = value;
    return value;
  }

  // ---------- rendering ----------
  function unitCentsOf(li) {
    return parseInt(li.dataset.wsUnit || li.dataset.unit) || 0;
  }

  function renderLine(li, lineCents, compareCents) {
    var el = li.querySelector('.cart-line-total');
    if (!el) return;
    var html = '';
    if (compareCents && compareCents > lineCents) {
      html += '<span class="old-price">' + fmt(compareCents) + '</span>&nbsp;';
    }
    html += '<span class="money">' + fmt(lineCents) + '</span>';
    el.dataset.wsWritten = String(lineCents);
    el.innerHTML = html;
  }

  function renderUnit(li, unitCents, retailUnit) {
    var el = li.querySelector('.cart-unit-price-amount');
    if (!el) return;
    var html = '';
    if (retailUnit && retailUnit > unitCents) {
      html += '<span class="old-price">' + fmt(retailUnit) + '</span>&nbsp;';
    }
    html += '<span class="money">' + fmt(unitCents) + '</span>';
    el.innerHTML = html;
  }

  function resyncLineNumbers(list) {
    rowsOf(list).forEach(function (li, i) {
      li.querySelectorAll('[data-line]').forEach(function (el) {
        el.dataset.line = String(i + 1);
      });
    });
  }

  function renderSummary(list, subtotalCents, count, cartData) {
    var panel = list.parentElement;
    if (!panel) return;
    var ws = isWholesale(list);

    var firstLine = panel.querySelector('.sticky-in-panel .l4tt li:first-child');
    if (firstLine) {
      var label = firstLine.querySelector('span:first-child');
      if (label) label.textContent = label.textContent.replace(/\(\d+\)/, '(' + count + ')');
      var sub = firstLine.querySelector('.drawer_sub');
      if (sub) sub.innerHTML = '<span class="money">' + fmt(subtotalCents) + '</span>';
    }

    // Cart-level discount amounts (they come from Shopify)
    if (cartData && cartData.cart_level_discount_applications) {
      cartData.cart_level_discount_applications.forEach(function (d) {
        panel.querySelectorAll('.l4tt li.overlay-gray').forEach(function (li) {
          if (li.dataset.discountTitle !== d.title) return;
          var amt = li.querySelector('.discount-amount');
          if (amt) amt.textContent = '(-' + fmt(d.total_allocated_amount) + ')';
        });
      });
    }

    var totalCents = cartData && !ws ? cartData.total_price : subtotalCents;

    // Free shipping bar
    var freeAmount = parseInt(list.dataset.freeAmount);
    var bar = panel.querySelector('.l4al li');
    if (bar && !isNaN(freeAmount)) {
      if (freeAmount > totalCents) {
        bar.className = 'overlay-rose';
        bar.innerHTML = (list.dataset.msgAway || '').replace('__AMOUNT__', fmt(freeAmount - totalCents));
      } else {
        bar.className = 'overlay-lime';
        bar.innerHTML = '<span class="overlay-content">' + (list.dataset.msgFree || '') + '</span>';
      }
    }

    // Header cart icon
    var countEl = document.getElementById('cart-count');
    if (countEl) countEl.textContent = count;
    var headerTotal = document.getElementById('cart-total');
    if (headerTotal) {
      var moneyEl = headerTotal.querySelector('.money') || headerTotal;
      moneyEl.textContent = fmt(totalCents);
    }
  }

  function allRowsWholesale(list) {
    var rows = rowsOf(list);
    return isWholesale(list) && rows.length > 0 && rows.every(function (li) { return !!li.dataset.wsUnit; });
  }

  // Instant, no-network recalculation from the unit prices we know
  function recalc(list) {
    if (!list) return;
    var total = 0;
    var count = 0;
    rowsOf(list).forEach(function (li) {
      var input = li.querySelector('input[name="updates[]"]');
      var qty = parseInt(input && input.value) || 0;
      var lineCents = unitCentsOf(li) * qty;
      var compare = li.dataset.wsUnit ? 0 : (parseInt(li.dataset.compareUnit) || 0) * qty;
      renderLine(li, lineCents, compare);
      total += lineCents;
      count += qty;
    });
    renderSummary(list, total, count, null);
  }

  function showEmpty(list) {
    var el = list.nextElementSibling;
    while (el) {
      if (el.tagName !== 'SCRIPT' && el.tagName !== 'STYLE') el.style.display = 'none';
      el = el.nextElementSibling;
    }
    var prev = list.previousElementSibling;
    if (prev && prev.querySelector('.m6pn-close')) prev.style.display = 'none';
    list.insertAdjacentHTML('beforebegin', '<p><span class="empty">' + (list.dataset.empty || '') + '</span></p>');
    renderSummary(list, 0, 0, null);
  }

  // Reconcile the drawer with what Shopify actually stored
  function syncFromCart(list, cartData) {
    if (!list || !cartData || !cartData.items) return;
    var byKey = {};
    cartData.items.forEach(function (item) { byKey[item.key] = item; });

    rowsOf(list).forEach(function (li) {
      var item = byKey[li.dataset.key];
      if (!item) { li.remove(); return; }

      var input = li.querySelector('input[name="updates[]"]');
      if (input && !pendingInputs.has(input) && parseInt(input.value) !== item.quantity) {
        input.value = item.quantity;
        updateStepper(input);
      }

      if (li.dataset.wsUnit) {
        renderLine(li, parseInt(li.dataset.wsUnit) * item.quantity, 0);
      } else {
        li.dataset.unit = String(item.final_price);
        var compareUnit = parseInt(li.dataset.compareUnit) || 0;
        renderUnit(li, item.final_price, compareUnit);
        renderLine(li, item.final_line_price, compareUnit * item.quantity);
      }
    });

    resyncLineNumbers(list);

    if (cartData.item_count === 0 || rowsOf(list).length === 0) {
      showEmpty(list);
      return;
    }

    if (allRowsWholesale(list)) {
      recalc(list);
    } else {
      renderSummary(list, cartData.items_subtotal_price, cartData.item_count, cartData);
    }
  }

  function runWsaioHooks() {
    if (typeof window.WSAIO === 'undefined') return;
    try {
      if (typeof WSAIO.load_checkout_restriction === 'function') {
        WSAIO.load_checkout_restriction();
      } else if (typeof WSAIO.checkout_restriction === 'function') {
        WSAIO.checkout_restriction();
      }
    } catch (err) {
      console.warn('[side-cart] WSAIO restriction hook failed:', err);
    }
  }

  // ---------- saving ----------
  function commitQty(input) {
    var list = listOf(input);
    if (!list) return Promise.resolve();
    return enqueue(function () {
      return postChange(input.dataset.line, clampToStep(input));
    }).then(function (cartData) {
      // The theme may have re-rendered the drawer meanwhile; use the live list
      syncFromCart(document.body.contains(list) ? list : document.querySelector('.side-cart-list'), cartData);
      runWsaioHooks();
    });
  }

  function scheduleQty(input, delay) {
    clearTimeout(qtyTimers.get(input));
    pendingInputs.add(input);
    qtyTimers.set(input, setTimeout(function () {
      pendingInputs.delete(input);
      commitQty(input);
    }, delay));
  }

  function removeRow(li) {
    var list = listOf(li);
    if (!list) return;
    var input = li.querySelector('input[name="updates[]"]');
    if (input) {
      clearTimeout(qtyTimers.get(input));
      pendingInputs.delete(input);
    }
    var lineEl = li.querySelector('[data-line]');
    var line = lineEl && lineEl.dataset.line;
    if (!line) return;

    li.remove();
    resyncLineNumbers(list);
    if (rowsOf(list).length === 0) {
      showEmpty(list);
    } else {
      recalc(list);
    }

    enqueue(function () { return postChange(line, 0); }).then(function (cartData) {
      if (rowsOf(list).length > 0) syncFromCart(list, cartData);
      runWsaioHooks();
    });
  }

  // ---------- events ----------
  // Registered on window in the capture phase so they run before the
  // theme's drawer handlers, which are then stopped.
  window.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.side-cart-list .ecom-qty-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (btn.disabled) return;

    var stepper = btn.closest('.cart-qty-stepper');
    var input = stepper && stepper.querySelector('input[name="updates[]"]');
    var li = btn.closest('li[data-key]');
    if (!input || !li) return;

    var min = parseInt(input.min) || 1;
    var step = parseInt(input.step) || 1;
    var max = parseInt(input.max);
    var current = parseInt(input.value) || min;

    if (btn.classList.contains('ecom-qty-minus')) {
      if (current - step < min) { removeRow(li); return; }
      input.value = current - step;
    } else {
      if (!isNaN(max) && current + step > max) return;
      input.value = current + step;
    }

    updateStepper(input);
    recalc(listOf(li));
    scheduleQty(input, 400);
  }, true);

  window.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('.side-cart-list .remove-from-cart-link');
    if (!link) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var li = link.closest('li[data-key]');
    if (li) removeRow(li);
  }, true);

  window.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.name !== 'updates[]' || !input.closest('.side-cart-list')) return;
    e.stopImmediatePropagation();
    clampToStep(input);
    updateStepper(input);
    recalc(listOf(input));
    scheduleQty(input, 350);
  }, true);

  window.addEventListener('input', function (e) {
    var t = e.target;
    if (t && t.name === 'updates[]' && t.closest('.side-cart-list')) e.stopImmediatePropagation();
  }, true);

  // ---------- wholesale ----------
  // WSAIO writes the wholesale UNIT price into the row price element. Move
  // it to the unit line and show unit x quantity as the row total.
  function applyWholesaleRow(li) {
    var el = li.querySelector('.cart-line-total');
    if (!el) return;
    var cents = parseCents(priceText(el));
    if (cents === null) return;
    if (el.dataset.wsWritten && parseInt(el.dataset.wsWritten) === cents) return; // our own write
    if (cents === parseInt(el.dataset.retailLine)) return; // app hasn't rewritten it

    var input = li.querySelector('input[name="updates[]"]');
    var qty = parseInt(input && input.value) || 1;
    li.dataset.wsUnit = String(cents);
    renderLine(li, cents * qty, 0);

    // Wholesale customers see only their price, no struck-out retail
    renderUnit(li, cents, 0);
    markListReady(listOf(li));
  }

  // Reveal the hidden drawer prices once every row has its wholesale price
  function markListReady(list, force) {
    if (!list || list.classList.contains('ws-ready')) return;
    if (!force && !allRowsWholesale(list)) return;
    if (allRowsWholesale(list)) recalc(list); // write the wholesale subtotal ourselves
    list.classList.add('ws-ready');
  }

  function initRow(li) {
    if (li.dataset.enh) return;
    li.dataset.enh = '1';
    var input = li.querySelector('input[name="updates[]"]');
    if (input) updateStepper(input);

    var list = listOf(li);
    if (isWholesale(list)) {
      // Never leave drawer prices hidden if the app doesn't convert a row
      if (list && !list.dataset.wsTimer) {
        list.dataset.wsTimer = '1';
        setTimeout(function () { markListReady(list, true); }, 3000);
      }
      var el = li.querySelector('.cart-line-total');
      if (el) {
        applyWholesaleRow(li);
        new MutationObserver(function () { applyWholesaleRow(li); })
          .observe(el, { childList: true, subtree: true, characterData: true });
      }
    }
  }

  // ---------- header total (wholesale) ----------
  // The theme writes Shopify's retail total into the header cart icon on
  // page load and after add-to-cart. For wholesale customers, replace it
  // with the sum of the wholesale rows, from the cart page if we're on it,
  // otherwise from the drawer. Skipped unless every row in that list has a
  // wholesale price and its quantities match the header count, so a stale
  // list never writes a wrong total.
  function wholesaleRows() {
    var containers = [
      document.querySelector('.cart-page-items'),
      document.querySelector('.side-cart-list')
    ];
    for (var i = 0; i < containers.length; i++) {
      var c = containers[i];
      if (!c) continue;
      var rows = Array.prototype.slice.call(c.querySelectorAll(':scope > li[data-key]'));
      if (!rows.length) continue;
      if (rows.every(function (li) { return !!li.dataset.wsUnit; })) return rows;
    }
    return null;
  }

  // Header-total cache: remembers "retail text + item count -> wholesale
  // total" for this browser session, so the next page can show the
  // wholesale figure immediately instead of flashing retail first.
  // theme.liquid hides #cart-total for wholesale customers until
  // <html> gets the ws-header-done class (see the install notes).
  var HEADER_CACHE_KEY = 'wsHeaderTotals';

  function readHeaderCache() {
    try { return JSON.parse(sessionStorage.getItem(HEADER_CACHE_KEY)) || {}; } catch (e) { return {}; }
  }

  function writeHeaderCache(key, cents) {
    try {
      var cache = readHeaderCache();
      cache[key] = cents;
      var keys = Object.keys(cache);
      if (keys.length > 20) delete cache[keys[0]]; // keep it small
      sessionStorage.setItem(HEADER_CACHE_KEY, JSON.stringify(cache));
    } catch (e) {}
  }

  function headerParts() {
    var headerTotal = document.getElementById('cart-total');
    if (!headerTotal) return null;
    var countEl = document.getElementById('cart-count');
    return {
      moneyEl: headerTotal.querySelector('.money') || headerTotal,
      count: countEl ? parseInt(countEl.textContent) : NaN
    };
  }

  function revealHeaderTotal() {
    document.documentElement.classList.add('ws-header-done');
  }

  // On page load: if we've already seen this exact retail total and count,
  // show the remembered wholesale total right away.
  function applyCachedHeaderTotal() {
    var parts = headerParts();
    if (!parts) return;
    var cached = readHeaderCache()[parts.count + '|' + parts.moneyEl.textContent.trim()];
    if (typeof cached === 'number') {
      parts.moneyEl.textContent = fmt(cached);
      revealHeaderTotal();
    }
  }

  function syncHeaderTotal() {
    var isWsCustomer =
      document.querySelector('.side-cart-list[data-wholesale="true"]') ||
      document.querySelector('.cart-page-items li[data-ws-unit]');
    if (!isWsCustomer) return;

    var rows = wholesaleRows();
    if (!rows) return;

    var total = 0;
    var count = 0;
    rows.forEach(function (li) {
      var input = li.querySelector('input[name="updates[]"]');
      var qty = parseInt(input && input.value) || 0;
      total += (parseInt(li.dataset.wsUnit) || 0) * qty;
      count += qty;
    });

    var parts = headerParts();
    if (!parts) return;
    if (!isNaN(parts.count) && parts.count !== count) return; // list is stale

    var current = parts.moneyEl.textContent.trim();
    if (parseCents(current) !== total) {
      // What's showing now is the theme's retail figure: remember the pair
      writeHeaderCache(count + '|' + current, total);
      parts.moneyEl.textContent = fmt(total);
    }
    revealHeaderTotal();
  }

  function scan() {
    document.querySelectorAll('.side-cart-list > li[data-key]').forEach(initRow);
    syncHeaderTotal();
  }

  function start() {
    applyCachedHeaderTotal();
    // Never leave the header total hidden if the wholesale total can't be
    // worked out (e.g. the drawer isn't on the page yet)
    setTimeout(revealHeaderTotal, 3000);

    // Pick up rows the theme injects when it renders or re-renders the drawer
    var scanQueued = false;
    new MutationObserver(function () {
      if (scanQueued) return;
      scanQueued = true;
      requestAnimationFrame(function () { scanQueued = false; scan(); });
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
    scan();
  }

  if (document.body) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
